import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useMatch, useSearchParams } from 'react-router-dom';

import Container from '@mui/material/Container';

import { useAccountHostingList, useUserHosting } from '@newfold/huapi-js';
import {
  AccountEventList200,
  AccountHostingList200AccountsItem,
  UserHosting200RowsItem,
} from '@newfold/huapi-js/src/index.schemas';

import useAccountEvents, {
  eventWatchRequestPropOptions,
} from '~/hooks/useAccountEvents';
import useAMRevamp from '~/hooks/useAMRevamp';
import useSessionStorage from '~/hooks/useSessionStorage';
import useSiteTabPolling, {
  type SiteTabRegistry,
} from '~/Nova/scenes/Site/scenes/AddWebsite/hooks/useSiteTabPolling';

import Main from '../Main';
import { useMFEContext } from '../MFEProvider';
import { useHttpErrorContext } from '../MFEProvider/lib/HttpClient';
import TenantError from './components/TenantError';
import {
  HOSTING_FETCH_LIMIT,
  HOSTING_LIST_PAGE_SS_KEY,
  HOSTING_MAX_FETCH_LIMIT,
} from './utils/constants';
import {
  getAiProductRouteTenantState,
  getAMRevampState,
  getLegacyState,
  getRetryDelay,
  isAiProductRoute,
  shouldRetry,
} from './utils/tenantHelpers';

export type EventWatchCallback = (
  eventArgs: eventWatchRequestPropOptions,
) => void;

type PaginationProps = {
  page: number;
  limit: number;
};

type TenantContextType =
  | {
      accountHostingList: AccountHostingList200AccountsItem[];
      callbacks: { subscribeToEvent: EventWatchCallback };
      eventsData: AccountEventList200 | undefined;
      isValidated: boolean;
      isProvisioning: boolean;
      isEmpty: boolean;
      isUnexpectedError: boolean;
      isTimeoutError: boolean;
      isPending: boolean;
      isError: boolean;
      refetch: () => void;
      hostingPackages?: UserHosting200RowsItem[];
      hostingPackagesTotal?: number;
      isHostingPackagesLoading?: boolean;
      isHostingPackagesError?: boolean;
      fetchHostingPackages?: (pageParam?: number, limitParam?: number) => void;
      resetHostingPackages?: () => void;
      isHostingPackagesEmpty?: boolean;
      page?: number;
      registerPendingSite?: (
        siteId: number,
        tab: Window | null,
        hostingId: string | number,
        windowName?: string,
      ) => void;
      siteTabsRef?: React.MutableRefObject<SiteTabRegistry>;
      showLoadMoreButton?: boolean;
    }
  | undefined;

const TenantContext = createContext<TenantContextType>(undefined);

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

const TenantProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { isAMRevamp } = useAMRevamp();
  const { error } = useHttpErrorContext();
  const { callbacks, data: events } = useAccountEvents();
  const eventsData = events?.data;
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const skipHostingFetch = isAiProductRoute(pathname);
  const isWebSitesPage = !!useMatch('/:hostingId?/sites/*');
  const isSinglePackageRoute = !!useMatch('/:hostingId?/package/websites/*');
  const shouldExcludeAiSite = !isWebSitesPage && !skipHostingFetch; // true on hosting list, false on Websites and AI product routes
  const hostingId = isSinglePackageRoute ? searchParams.get('packageId') : null;
  const {
    // @ts-expect-error
    hostingStatus: { isProvisioning: mfeProvisioning } = {},
  } = useMFEContext();

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: hostingId ? HOSTING_MAX_FETCH_LIMIT : HOSTING_FETCH_LIMIT,
  });
  const { page, limit } = pagination;
  const [cumulativeHostingPackages, setCumulativeHostingPackages] = useState<
    UserHosting200RowsItem[]
  >([]);

  const { registerPendingSite, siteTabsRef } = useSiteTabPolling();
  // eslint-disable-next-line
  const [hostingListPage] = useSessionStorage(HOSTING_LIST_PAGE_SS_KEY, 1);

  // Legacy API
  const {
    data: tenant,
    refetch: legacyRefetch,
    isLoading: legacyLoading,
    isPending: legacyPending,
    isError: legacyError,
    failureCount: legacyFailureCount,
    failureReason: legacyFailureReason,
  } = useAccountHostingList(undefined, {
    query: {
      enabled: !isAMRevamp,
      staleTime: 0,
      retryDelay: getRetryDelay,
      retry: (failureCount, error) =>
        shouldRetry(failureCount, error, mfeProvisioning),
    },
  });

  // Revamp API
  const {
    data: accountFeatureData,
    isLoading: revampLoading,
    isFetching: revampFetching,
    isError: revampError,
    failureCount: revampFailureCount,
    failureReason: revampFailureReason,
    refetch: revampRefetch,
  } = useUserHosting(
    { page, limit, remote: true, exclude_ai_site: shouldExcludeAiSite },
    {
      query: {
        enabled: isAMRevamp && !skipHostingFetch,
        select: (data) => data?.data,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        retryDelay: getRetryDelay,
        retry: (failureCount, error) => shouldRetry(failureCount, error, false),
      },
    },
  );

  // Manage cumulative data for revamp API
  useEffect(() => {
    if (!isAMRevamp || !accountFeatureData?.rows) return;
    const rowsLength = accountFeatureData.rows.length;
    setCumulativeHostingPackages((prev) =>
      page === 1
        ? accountFeatureData.rows ?? []
        : [...prev, ...(accountFeatureData.rows ?? [])],
    );
    if (rowsLength > 0) {
      if (page === 1 && limit > HOSTING_FETCH_LIMIT) {
        const actualPagesLoaded = Math.ceil(rowsLength / HOSTING_FETCH_LIMIT);
        sessionStorage.setItem(
          HOSTING_LIST_PAGE_SS_KEY,
          actualPagesLoaded.toString(),
        );
      } else {
        sessionStorage.setItem(HOSTING_LIST_PAGE_SS_KEY, page.toString());
      }
    }
  }, [isAMRevamp, accountFeatureData, page, limit]);

  // Reset cumulative data and pagination when hostingId changes
  useEffect(() => {
    if (isAMRevamp) {
      const nextPage = Number(
        sessionStorage.getItem(HOSTING_LIST_PAGE_SS_KEY) ?? 1,
      );
      setPagination({
        page: 1,
        limit: nextPage * HOSTING_FETCH_LIMIT,
      });
    }
  }, [hostingId, isAMRevamp]);

  // Refetch on error
  useEffect(() => {
    if (error && typeof tenantState.refetch === 'function')
      tenantState.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Memoize hostingPackages for context
  const hostingPackages = useMemo(
    () => (isAMRevamp ? cumulativeHostingPackages : []),
    [isAMRevamp, cumulativeHostingPackages],
  );
  const hostingPackagesTotal = isAMRevamp
    ? cumulativeHostingPackages.length
    : 0;
  const isHostingPackagesEmpty = hostingPackages.length === 0;

  // Show Load More button logic for revamp API
  const showLoadMoreButton = useMemo(() => {
    if (!isAMRevamp) return false;
    const rowsLength = accountFeatureData?.rows?.length ?? 0;
    if (page === 1 && limit > HOSTING_FETCH_LIMIT) {
      // If we got less than the limit, we've reached the end
      if (rowsLength < limit) return false;
    }
    if (page > 1 && rowsLength < HOSTING_FETCH_LIMIT && !revampFetching) {
      return false;
    }
    return (
      (rowsLength >= HOSTING_FETCH_LIMIT || revampLoading || revampFetching) &&
      !revampError
    );
  }, [
    isAMRevamp,
    accountFeatureData?.rows,
    revampLoading,
    revampFetching,
    revampError,
    page,
    limit,
  ]);

  // Compute tenant state
  const tenantState = useMemo(() => {
    if (isAMRevamp && skipHostingFetch) {
      return getAiProductRouteTenantState(revampRefetch);
    }

    return isAMRevamp
      ? getAMRevampState({
          revampData: accountFeatureData,
          revampLoading,
          revampError,
          revampFailureCount,
          revampFailureReason,
          revampRefetch,
          hostingPackages,
          isHostingPackagesEmpty,
          legacyLoading: false,
          legacyPending: false,
          legacyFailureCount: 0,
        })
      : getLegacyState({
          tenant,
          legacyLoading,
          legacyPending,
          legacyError,
          legacyFailureCount,
          legacyFailureReason,
          legacyRefetch,
          mfeProvisioning,
          revampLoading: false,
          revampFailureCount: 0,
          hostingPackages: [],
          isHostingPackagesEmpty: false,
        });
  }, [
    isAMRevamp,
    skipHostingFetch,
    accountFeatureData,
    revampLoading,
    revampError,
    revampFailureCount,
    revampFailureReason,
    revampRefetch,
    hostingPackages,
    isHostingPackagesEmpty,
    tenant,
    legacyLoading,
    legacyPending,
    legacyError,
    legacyFailureCount,
    legacyFailureReason,
    legacyRefetch,
    mfeProvisioning,
  ]);

  const fetchHostingPackages = useCallback(
    (pageParam: number = 1, limitParam: number = HOSTING_FETCH_LIMIT) => {
      if (isAMRevamp) {
        setPagination({ page: pageParam, limit: limitParam });
      }
    },
    [isAMRevamp],
  );

  const resetHostingPackages = useCallback(() => {
    if (!isAMRevamp) return;
    setCumulativeHostingPackages([]);
    sessionStorage.setItem(HOSTING_LIST_PAGE_SS_KEY, '1');
    setPagination({
      page: 1,
      limit: hostingId ? HOSTING_MAX_FETCH_LIMIT : HOSTING_FETCH_LIMIT,
    });
    revampRefetch?.();
  }, [isAMRevamp, revampRefetch, hostingId]);

  const {
    accountHostingList,
    isPending,
    isError,
    refetch,
    isValidated,
    isEmpty,
    isTimeoutError,
    isUnexpectedError,
    isProvisioningExtended,
    showTenantError,
  } = tenantState;

  return (
    <TenantContext.Provider
      value={{
        accountHostingList,
        callbacks,
        eventsData,
        isEmpty,
        isValidated,
        isProvisioning: isProvisioningExtended,
        isUnexpectedError,
        isTimeoutError,
        isPending,
        isError,
        refetch,
        ...(isAMRevamp && {
          hostingPackages,
          hostingPackagesTotal,
          isHostingPackagesLoading: revampLoading || revampFetching,
          isHostingPackagesError: revampError,
          fetchHostingPackages,
          resetHostingPackages,
          isHostingPackagesEmpty,
          page,
          registerPendingSite,
          siteTabsRef,
          showLoadMoreButton,
        }),
      }}
    >
      {showTenantError ? (
        <Main>
          <Container>
            <TenantError />
          </Container>
        </Main>
      ) : (
        children
      )}
    </TenantContext.Provider>
  );
};

export default TenantProvider;
